import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import I from "./Icons";
import { pick } from "../lib/i18n";
import BoxSelector from "./BoxSelector";
import type { NormalizedBox } from "./BoxSelector";

type ReferencePhoto = { id: number; url: string };

type Props = {
  sourceImageUrl: string;
  referencePhotos: ReferencePhoto[];
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (params: { referenceFileId: number; sourceBox: NormalizedBox; referenceBox: NormalizedBox; note: string }) => Promise<void>;
};

const MIN_BOX_SIZE = 0.03;
const NOTE_MAX_LENGTH = 200;

const copy = {
  pl: {
    title: "Popraw fragment",
    intro: "Zaznacz zły fragment na wygenerowanej grafice, a potem ten sam fragment na prawdziwym zdjęciu produktu - zaznaczenia wskażą modelowi miejsce korekty. Sprawdź cały wynik po zmianie.",
    stepSource: "1. Zaznacz obszar do poprawy",
    stepReference: "2. Wybierz zdjęcie referencyjne i zaznacz ten sam fragment",
    noteLabel: "Krótka notatka (opcjonalnie)",
    noteHint: "Np. „przywróć kształt i mocowanie uchwytu”.",
    cancel: "Anuluj",
    submit: "Popraw fragment",
    submitting: "Przygotowuję…",
    close: "Zamknij",
    needBoth: "Zaznacz obszar na obu zdjęciach, żeby kontynuować.",
    noPhotos: "Brak zdjęć referencyjnych produktu do wyboru.",
  },
  en: {
    title: "Spot-fix a region",
    intro: "Mark the bad area on the generated image, then the same area on a real product photo - the boxes guide the correction. Review the complete result afterwards.",
    stepSource: "1. Mark the area to fix",
    stepReference: "2. Pick a reference photo and mark the same area there",
    noteLabel: "Short note (optional)",
    noteHint: "E.g. “restore the handle shape and attachment”.",
    cancel: "Cancel",
    submit: "Fix this spot",
    submitting: "Preparing…",
    close: "Close",
    needBoth: "Mark an area on both images to continue.",
    noPhotos: "No product reference photos available to pick from.",
  },
};

export default function SpotFixDialog({ sourceImageUrl, referencePhotos, submitting, error, onClose, onSubmit }: Props) {
  const t = pick(copy);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [sourceBox, setSourceBox] = useState<NormalizedBox | null>(null);
  const [referenceFileId, setReferenceFileId] = useState<number | null>(referencePhotos[0]?.id ?? null);
  const [referenceBox, setReferenceBox] = useState<NormalizedBox | null>(null);
  const [note, setNote] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const errorMessage = validationError ?? error;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const selectedReference = referencePhotos.find((p) => p.id === referenceFileId) ?? null;

  const boxIsValid = (box: NormalizedBox | null) => !!box && box.w >= MIN_BOX_SIZE && box.h >= MIN_BOX_SIZE;

  const handleCancel = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!boxIsValid(sourceBox) || !boxIsValid(referenceBox) || !referenceFileId) {
      setValidationError(t.needBoth);
      return;
    }

    setValidationError(null);
    await onSubmit({
      referenceFileId,
      sourceBox: sourceBox!,
      referenceBox: referenceBox!,
      note: note.trim(),
    });
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="spot-fix-title"
      onCancel={(event) => {
        event.preventDefault();
        handleCancel();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) handleCancel();
      }}
      style={{
        width: "min(94vw, 44rem)",
        margin: "auto",
        padding: 0,
        border: "1px solid var(--line, var(--border))",
        borderRadius: 12,
        background: "var(--surface, var(--panel))",
        color: "var(--ink)",
        boxShadow: "0 24px 80px oklch(0 0 0 / .35)",
      }}
    >
      <form onSubmit={handleSubmit} style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 id="spot-fix-title" style={{ margin: 0, fontSize: 18 }}>{t.title}</h2>
            <p style={{ margin: "6px 0 0", color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>{t.intro}</p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" aria-label={t.close} onClick={handleCancel} disabled={submitting}>
            {I.x}
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{t.stepSource}</span>
          <BoxSelector src={sourceImageUrl} box={sourceBox} onChange={setSourceBox} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{t.stepReference}</span>
          {referencePhotos.length === 0 ? (
            <p style={{ margin: 0, color: "var(--muted)", fontSize: 12 }}>{t.noPhotos}</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {referencePhotos.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => {
                      setReferenceFileId(photo.id);
                      setReferenceBox(null);
                    }}
                    disabled={submitting}
                    style={{
                      padding: 0,
                      width: 56,
                      height: 56,
                      borderRadius: 6,
                      overflow: "hidden",
                      border: photo.id === referenceFileId ? "2px solid var(--accent)" : "1px solid var(--line)",
                      background: "var(--bg-2)",
                      cursor: "pointer",
                    }}
                  >
                    <img src={photo.url} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </button>
                ))}
              </div>
              {selectedReference && (
                <BoxSelector key={selectedReference.id} src={selectedReference.url} box={referenceBox} onChange={setReferenceBox} />
              )}
            </>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="spot-fix-note" style={{ fontSize: 13, fontWeight: 600 }}>{t.noteLabel}</label>
          <input
            id="spot-fix-note"
            type="text"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={NOTE_MAX_LENGTH}
            disabled={submitting}
            style={{ width: "100%", boxSizing: "border-box" }}
          />
          <p style={{ margin: 0, color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>{t.noteHint}</p>
        </div>

        {errorMessage && <p role="alert" style={{ margin: 0, color: "var(--danger, #c0392b)", fontSize: 12 }}>{errorMessage}</p>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={handleCancel} disabled={submitting}>{t.cancel}</button>
          <button type="submit" className="btn btn-primary" disabled={submitting || referencePhotos.length === 0}>
            {submitting ? t.submitting : t.submit}
          </button>
        </div>
      </form>
    </dialog>
  );
}
